import { action, computed, observable } from "mobx";

import { LSWidgetElement } from "@data-types/live-widget/LSwidgetElement";
import { WidgetInfo } from "@data-types/live-widget/WidgetInfo";
import { WidgetLocation } from "@data-types/live-widget/WidgetLocation";

import { IStoreLifeCycle } from "@interface";

import { LiveStreamWidget } from "@state/stores/live-widgets/LiveStreamWidget";
import { TLiveStreamWidgetConfig } from "@data-types/live-widget/TLiveStreamWidgetConfig";

export class LiveStreamState implements IStoreLifeCycle {
	@observable private lssDisabled = false;
	@observable private elements: LiveStreamWidget[] = observable.array([]);
	@observable isStoreInitialized: boolean = false;

	@computed public get activeWidgets() {
		return this.elements;
	}

	@computed public get findActiveLiveStream() {
		return (
			eventId: string,
			location: WidgetLocation
		): LiveStreamWidget | undefined => {
			return this.activeWidgets.find(
				(w) => w.location == location && w.eventId === eventId
			);
		};
	}

	@computed public get activeSideWidget() {
		return this.activeWidgets.find(
			(w) => w.location === WidgetLocation.SIDE
		);
	}

	@computed get isEmpty(): boolean {
		return this.elements.length === 0;
	}

	//#region constructor

	@action.bound
	onInitialize() {
		this.isStoreInitialized = true;
	}

	//#endregion constructor

	//#region add remove widget

	@action.bound
	toggleWidgetPin(eventId: string, location: WidgetLocation): void {
		const widget = this.activeWidgets.find(
			(w) => w.eventId === eventId && w.location === location
		);
		if (widget == null) {
			return;
		}

		this.updateItem({ ...widget, isPinned: !widget.isPinned });
	}

	/** Toggles widget for given eventId on location. */
	@action.bound
	toggleLiveStream(config: TLiveStreamWidgetConfig): void {
		if (this.findActiveLiveStream(config.eventId, config.location)) {
			this.removeLiveStream(config.eventId, config.location);
		} else {
			this.addLiveStream(config);
		}
	}

	/** Add ls widget, removes one from queue if it is at max length */
	@action.bound
	addLiveStream(config: TLiveStreamWidgetConfig): void {
		if (this.lssDisabled) {
			if (config.location === WidgetLocation.SIDE) {
				this.removeLiveStream("", WidgetLocation.SIDE);
			}
			return;
		}

		if (
			!this.isInQueue({
				eventId: config.eventId,
				location: config.location,
			})
		) {
			this.elements.push(this.createLiveStream(config));
		} else {
			this.updateItem(this.createLiveStream(config));
		}
	}

	createLiveStream(config: TLiveStreamWidgetConfig): LiveStreamWidget {
		const widget = new LiveStreamWidget(
			config.eventId,
			config.location,
			config.teamOneName,
			config.teamTwoName
		);
		widget.isUserInitiated = config.isUserInitiated ?? false;
		widget.autoPlay = config.autoPlay ?? config.isUserInitiated ?? false;
		return widget;
	}

	/** Update item for given search object if it is in queue. */
	@action.bound
	public updateItem(newItem: LiveStreamWidget): void {
		const index = this.elements.findIndex((el) =>
			this.isWidgetActive(el, {
				eventId: newItem.eventId,
				location: newItem.location,
			})
		);
		if (index < 0 || index >= this.elements.length) {
			return;
		}

		this.elements[index] = newItem;
	}
	@action.bound
	public replaceWidgetMessage(
		widget: LiveStreamWidget,
		newItem: LiveStreamWidget["message"]
	): void {
		const index = this.elements.findIndex((el) =>
			this.isWidgetActive(el, {
				eventId: widget.eventId,
				location: widget.location,
			})
		);
		if (index < 0 || index >= this.elements.length) {
			return;
		}

		this.elements[index].message = newItem;
	}

	/** @param element remove from queue. */
	@action.bound
	remove(element: WidgetInfo): void {
		if (this.isEmpty) {
			return;
		}

		const elemIdx = this.elements.findIndex((el) =>
			this.isWidgetActive(el, element)
		);

		if (elemIdx > -1) {
			this.elements.splice(elemIdx, 1);
		}
	}

	/** Disable lmts and disposes all active widgets */
	@action.bound
	removeLiveStream(eventId: string, location: WidgetLocation): void {
		this.remove({ eventId, location });
	}

	//#endregion add remove widget

	//#region helpers

	/** @returns element is in queue based on passed predicate */
	public isInQueuePredicate(
		element: WidgetInfo,
		predicate: (a: LSWidgetElement, b: WidgetInfo) => boolean
	): boolean {
		if (element == null || predicate == null) {
			return false;
		}

		return this.elements.findIndex((el) => predicate(el, element)) > -1;
	}

	@action.bound
	private isWidgetActive(w: LSWidgetElement, searchArgs: WidgetInfo) {
		return (
			(searchArgs.location === WidgetLocation.SIDE &&
				w.location === WidgetLocation.SIDE) ||
			(w.location == searchArgs.location &&
				w.eventId === searchArgs.eventId)
		);
	}

	/** @returns element is in queue based on filterPredicate given to constructor */
	public isInQueue(element: WidgetInfo): boolean {
		return this.isInQueuePredicate(element, this.isWidgetActive);
	}

	//#endregion helpers

	//#region disposers

	/** Disable lmts and disposes all active widgets */
	@action.bound
	disableLss(): void {
		this.lssDisabled = true;
		this.onDispose();
	}

	/** Remove all active widgets */
	@action.bound
	onDispose(): void {
		this.elements = [];
		this.isStoreInitialized = false;
	}

	//#endregion disposers
}
